import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { MenuBarItem, StatusBarItem } from "./types";

interface WorkspaceContextValue {
	/** Set menu bar items from the active module */
	setMenuBar: (items: MenuBarItem[]) => void;
	/** Set status bar items from the active page */
	setStatusBar: (items: StatusBarItem[]) => void;
	/** Current module menu bar items */
	menuBar: MenuBarItem[];
	/** Current page status bar items */
	statusBar: StatusBarItem[];
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceContextProvider({
	children,
}: { children: ReactNode }) {
	const [menuBar, setMenuBarState] = useState<MenuBarItem[]>([]);
	const [statusBar, setStatusBarState] = useState<StatusBarItem[]>([]);

	const setMenuBar = useCallback((items: MenuBarItem[]) => {
		setMenuBarState(items);
	}, []);

	const setStatusBar = useCallback((items: StatusBarItem[]) => {
		setStatusBarState(items);
	}, []);

	const value = useMemo(
		() => ({
			setMenuBar,
			setStatusBar,
			menuBar,
			statusBar,
		}),
		[setMenuBar, setStatusBar, menuBar, statusBar],
	);

	return (
		<WorkspaceContext.Provider value={value}>
			{children}
		</WorkspaceContext.Provider>
	);
}

export function useWorkspaceContext(): WorkspaceContextValue {
	const ctx = useContext(WorkspaceContext);
	if (!ctx) {
		throw new Error(
			"useWorkspaceContext must be used within a WorkspaceContextProvider",
		);
	}
	return ctx;
}
